import {useApolloClient} from '@apollo/client';
import {
  DepositoryType,
  useAccountQuery,
  useAllRawTransactionsLazyQuery,
  useBalanceAnchorsQuery,
  useBalanceReadingsQuery,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {Plus} from 'lucide-react';
import * as React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import updateRawTransactions from 'src/apolloCache/updateRawTransactions';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import {DateFilterSelect} from 'src/components/DateFilterSelect';
import {ResetFiltersButton} from 'src/components/ResetFiltersButton';
import {useUrlFilterParams} from 'src/hooks/useUrlFilterParams';
import Empty from 'src/ListView/Empty';
import {MoreButton} from 'src/ListView/MoreButton';
import Loading from 'src/Loading';
import {useModals} from 'src/Modals';
import RawTransactionList from 'src/RawTransactionList';
import {
  AllocationFilterSelect,
  parseAllocationFilter,
} from 'src/RawTransactionListView/AllocationFilterSelect';
import {Header} from 'src/RawTransactionListView/Header';
import {
  ReconcileSessionHeader,
  ReconcileSessionProvider,
  ReconcileSessionSummaryFooter,
  ReconcileTimeline,
} from 'src/RawTransactionListView/ReconcileTimeline';
import {useRawRowGestures} from 'src/RawTransactionListView/useRawRowGestures';
import {useRawSelectionActions} from 'src/RawTransactionListView/useRawSelectionActions';
import {fixUpDescription} from 'src/reconcile/useReconcileSession';
import {ScrollTable} from 'src/ScrollTable';
import {SearchInput} from 'src/SearchInput';
import {useSelectionState} from 'src/useSelectionState';
import type {
  AllRawTransactionsQuery,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateFilterState} from 'src/components/DateFilterSelect';

type Props = {
  includeProcessed: boolean;
  /**
   * 'browse' (default): the All/Allocate workflows with benefit, date, and
   * search filters. 'reconcile': the same list pinned to the reconcile
   * timeline with balance anchors — a separate workflow that always runs
   * over the complete, unfiltered row set.
   */
  mode?: 'browse' | 'reconcile';
};

const EMPTY_DATE_FILTER: DateFilterState = {startDate: null, endDate: null};

// Must match the duration of RawTransactionRow's leaving transition — the row
// is unmounted (via the cache update) when this elapses, so a shorter value
// would clip the animation and a longer one would leave a gap of collapsed
// rows sitting at zero height.
const ROW_EXIT_MS = 200;

const RawTransactionView = (props: Props) => {
  const navigate = useNavigate();
  const params = useParams();
  const {recordBalanceAnchorModal, createRawTransactionModal} = useModals();
  // Skip when embedded in AccountTransactionsView; the AccountDetail layout
  // route owns the crumb there.
  useBreadcrumb(params.id ? null : {label: 'Unreconciled', reset: true});
  // Reconcile is a separate workflow (its own account tab), pinned to the
  // complete unfiltered row set. Browse mode carries the allocation, date, and
  // search filters.
  const isReconcile = props.mode === 'reconcile';
  // Browse filters live in the URL query string so they survive navigation:
  // opening a transaction and hitting back returns to the same filtered list.
  // Because the query string is path-scoped (each account's Transactions tab is
  // its own URL), navigating to another account starts from the default
  // (unfiltered) view without any per-account bookkeeping. Reconcile mode has
  // its own route and ignores these entirely (see activeDateFilter below).
  const [searchParams, setParams] = useUrlFilterParams();
  const allocationFilter = parseAllocationFilter(searchParams.get('allocation'));
  const dateFilter: DateFilterState = {
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
  };
  const search = searchParams.get('search') ?? '';
  // Date and search narrowing are excluded from Reconcile: anchors and their
  // deltas are account-wide, and a gap in the timeline is only visible on the
  // full, unfiltered row set.
  const activeDateFilter = isReconcile ? EMPTY_DATE_FILTER : dateFilter;
  const activeSearch = isReconcile ? '' : search;
  // The browse filters currently narrowing the list, so Reset can clear them
  // in one history entry and the control can hide when there is nothing to
  // clear. Empty in reconcile mode, which ignores these entirely.
  const activeFilterKeys = isReconcile
    ? []
    : [
        allocationFilter !== 'all' ? 'allocation' : null,
        dateFilter.startDate != null ? 'startDate' : null,
        dateFilter.endDate != null ? 'endDate' : null,
        search !== '' ? 'search' : null,
      ].filter((key): key is string => key != null);

  const resetFilters = () => {
    setParams(Object.fromEntries(activeFilterKeys.map((key) => [key, null])));
  };
  const filters = {
    includeProcessed: props.includeProcessed,
    accountId: params.id,
    // The allocation filter narrows server-side (Unallocated is a to-do
    // queue). The Reconcile view paginates unreconciled rows only: claimed
    // history is summarized by its anchor rows and loads lazily on expansion,
    // so pages aren't consumed by rows hidden inside collapsed spans.
    // Unreconciled rows are exactly the interesting ones — gaps and stragglers
    // surface within the first pages instead of behind a span's worth of
    // clicks.
    allocated: isReconcile
      ? undefined
      : allocationFilter === 'allocated'
        ? true
        : allocationFilter === 'unallocated'
          ? false
          : undefined,
    reconciled: isReconcile ? false : undefined,
    startDate: activeDateFilter.startDate,
    endDate: activeDateFilter.endDate,
    search: activeSearch || undefined,
    // Unreconciled rows are sparse (a page can span years of reconciled
    // history), so the reconcile view fetches bigger pages.
    limit: isReconcile ? 50 : 10,
  };

  // The Reconcile view interleaves balance anchors into the timeline —
  // reconciled anchors plus manual/statement balances awaiting
  // reconciliation (Plaid sync readings live in their own collection and
  // never appear here).
  const {
    data: anchorsData,
    loading: anchorsLoading,
    refetch: refetchAnchors,
  } = useBalanceAnchorsQuery({
    variables: {accountId: params.id ?? ''},
    skip: !params.id || !isReconcile,
  });
  const anchors = anchorsData?.balanceAnchors ?? [];
  // The "add starting balance" affordance keys off the absence of an earlier
  // observation, so it must not fire while the anchors are still loading: an
  // empty-because-pending list would offer to bootstrap an account that
  // already has a baseline, and saving would create a nearer anchor that
  // shifts the server's baseline selection for later reconciliations.
  const anchorsLoaded = anchorsData !== undefined && !anchorsLoading;

  // The reconcile session lives here (above the ScrollTable) so its footer
  // action bar can render outside the scroll area, pinned below the list. The
  // active anchor and its baseline (the nearest earlier anchor) drive the
  // summary card and row highlighting inside the timeline.
  const [activeAnchorId, setActiveAnchorId] = React.useState<string | null>(
    null,
  );
  // Anchors whose reconciliation succeeded but which the loaded data still
  // shows as unreconciled. They must not offer to start again in that window:
  // the server rejects a second reconciliation of the same anchor.
  const [completedAnchorIds, setCompletedAnchorIds] = React.useState<
    ReadonlySet<string>
  >(() => new Set());
  // Released by what the data says, not by a refetch resolving. Which response
  // won is not knowable from here — refetches can land out of order, and a
  // stale one can overwrite a newer anchor's reconciliation with null — so an
  // event-based release can free an anchor the data still shows unreconciled.
  // Reading the condition straight off `anchors` cannot disagree with what the
  // row renders, and it fails closed for free: a refetch that never lands
  // leaves the anchor locked, which is the truthful state of it.
  React.useEffect(() => {
    if (completedAnchorIds.size === 0) return;
    const stillLocked = [...completedAnchorIds].filter(
      (id) => !anchors.some((a) => a._id === id && a.reconciliation != null),
    );
    if (stillLocked.length !== completedAnchorIds.size) {
      setCompletedAnchorIds(new Set(stillLocked));
    }
  }, [anchors, completedAnchorIds]);

  const sortedAnchors = React.useMemo(
    () => [...anchors].sort((a, b) => b.date.localeCompare(a.date)),
    [anchors],
  );
  const activeAnchorIndex = sortedAnchors.findIndex(
    (a) => a._id === activeAnchorId,
  );
  const activeAnchor =
    activeAnchorIndex >= 0 ? sortedAnchors[activeAnchorIndex] : null;
  const activeBaseline =
    activeAnchor != null
      ? (sortedAnchors[activeAnchorIndex + 1] ?? null)
      : null;
  // A session runs only when the active anchor has a baseline to reconcile
  // against; while it does, the account's normal action bar is hidden.
  const reconcileSessionActive = activeAnchor != null && activeBaseline != null;

  // Anchor balances follow the credit-card display convention
  // (toDisplayBalance), so the timeline needs the account type. The account
  // detail layout has already fetched this account, so this resolves from
  // the cache.
  const {data: accountData, refetch: refetchAccount} = useAccountQuery({
    variables: {id: params.id ?? ''},
    skip: !params.id || !isReconcile,
  });
  const account = accountData?.account;
  const isCreditCard =
    account?.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard;
  const accountCurrency =
    account && 'currency' in account ? account.currency : undefined;

  // Whether a synced Plaid reading predates the earliest anchor. Readings are
  // not reconcilable and never appear in this timeline, but they are attested
  // balances the Overview chart draws — so an anchor with one below it is not
  // where the account's history starts, and must not be labeled as if it were
  // (the chart, which sees both collections, agrees). Readings are written
  // only by sync, so a non-Plaid account can't have any and the query is
  // skipped; `limit: 1` because this only ever asks whether one exists.
  const earliestAnchorDate =
    sortedAnchors.length > 0
      ? sortedAnchors[sortedAnchors.length - 1].date
      : null;
  const isPlaidLinked = account?.__typename === 'DepositoryAccount' &&
    account.plaid != null;
  const {data: earlierReadingsData} = useBalanceReadingsQuery({
    variables: {
      accountId: params.id ?? '',
      before: earliestAnchorDate ?? '',
      limit: 1,
    },
    skip: !params.id || !isReconcile || !isPlaidLinked || earliestAnchorDate == null,
  });
  // Unresolved counts as "yes" — the row must not claim to be the starting
  // balance and then take it back once the answer lands.
  const hasReadingBeforeAnchors =
    isPlaidLinked &&
    earliestAnchorDate != null &&
    (earlierReadingsData === undefined ||
      earlierReadingsData.balanceReadings.length > 0);

  const client = useApolloClient();
  const [loadRawTransactions, {loading, error, data, fetchMore}] =
    useAllRawTransactionsLazyQuery({
      notifyOnNetworkStatusChange: true,
    });
  const handleUpdateRawTransactions = updateRawTransactions({
    client,
    filters,
  });

  const all = data?.allRawTransactions?.items ?? [];
  const selectionState = useSelectionState<BasicRawTransactionFragment>(
    all,
    [],
  );
  const {selected, setSelected, setSelection, clearSelection} = selectionState;

  // The reconcile session auto-pages via an effect, which can re-fire while a
  // fetch is still settling — the cursor guard keeps a repeated call from
  // appending the same page twice (mirrors ReconcileAnchorModal's guard).
  const lastRequestedCursor = React.useRef<string | null>(null);

  const handleReload = () => {
    clearSelection();
    // Navigating to another account (or changing filters) ends any session,
    // and retires any completed-but-unrefreshed anchor with it — the list it
    // belonged to is gone.
    setActiveAnchorId(null);
    setCompletedAnchorIds(new Set());
    // A fresh base query invalidates the pagination guard — the old cursor
    // belongs to the previous list.
    lastRequestedCursor.current = null;
    loadRawTransactions({variables: filters});
  };
  React.useEffect(handleReload, [
    props.includeProcessed,
    params.id,
    allocationFilter,
    activeDateFilter.startDate,
    activeDateFilter.endDate,
    activeSearch,
  ]);

  const hasMore = data?.allRawTransactions?.hasMore ?? false;
  // Page from the query's own endCursor, not the last visible row: cache
  // updates can remove rows (reconciling claims them), and if every loaded
  // row is removed while hasMore is true, an item-derived cursor no longer
  // exists — but endCursor survives removals and always marks the true
  // fetch frontier.
  const endCursor = data?.allRawTransactions?.endCursor ?? null;

  const handleMore = () => {
    if (fetchMore && hasMore && endCursor) {
      if (loading || lastRequestedCursor.current === endCursor) {
        return;
      }
      lastRequestedCursor.current = endCursor;
      void fetchMore({
        variables: {
          ...filters,
          beforeCursor: endCursor,
        },
        updateQuery: (previous: AllRawTransactionsQuery, result) => {
          // Reconciling removes rows from the cached list; if the removed
          // tail included the cursor row, the next page can overlap rows
          // already loaded — dedupe by id instead of blindly appending.
          const existingIds = new Set(
            (previous.allRawTransactions?.items ?? []).map((rt) => rt._id),
          );
          const moreItems = (
            result.fetchMoreResult?.allRawTransactions?.items ?? []
          ).filter((rt) => !existingIds.has(rt._id));
          return {
            ...previous,
            allRawTransactions: {
              ...previous.allRawTransactions,
              items: [
                ...(previous.allRawTransactions?.items ?? []),
                ...moreItems,
              ],
              endCursor:
                result.fetchMoreResult?.allRawTransactions?.endCursor ?? null,
              hasMore:
                result.fetchMoreResult?.allRawTransactions?.hasMore ?? false,
            },
          };
        },
      }).catch(() => {
        // A transient failure must not pin the guard forever — clear it so
        // the More button and session auto-paging can retry this page.
        lastRequestedCursor.current = null;
      });
    }
  };

  const selectedIds = React.useMemo(
    () => selected.map((transaction) => transaction._id),
    [selected],
  );

  const toggleRow = (transaction: BasicRawTransactionFragment) => {
    const isSelected = selectedIds.includes(transaction._id);
    setSelected(transaction)(!isSelected);
  };

  const selectionActions = useRawSelectionActions({
    selectionState,
    onUpdate: handleUpdateRawTransactions,
    includeProcessed: props.includeProcessed,
    removeFullyConsumed: allocationFilter === 'unallocated',
    accountId: params.id,
  });
  // Browsing shares every activity list's gestures; the reconcile tab keeps
  // tap-to-select, since picking rows is how a reconciliation is built.
  const gestures = useRawRowGestures({
    selectionState,
    openCreate: selectionActions.openCreateForRow,
  });

  const endSession = () => {
    setActiveAnchorId(null);
    clearSelection();
  };

  // Rows a reconciliation just claimed, held in the list while they collapse
  // and fade out before the cache update removes them for real. Kept here
  // rather than in the timeline because the cache update is the owner's, and
  // the two have to stay in step: dropping the rows early would cut the
  // animation short, dropping them late would leave claimed rows in a list
  // that queries reconciled: false.
  const [leavingIds, setLeavingIds] = React.useState<ReadonlyArray<string>>([]);
  // The updater is carried with the pending removal rather than read at flush
  // time: it writes to the cache entry keyed by the filters in effect when the
  // reconcile happened, and those can change (switching accounts) before the
  // timer fires. Both paths out of here must target that same entry.
  const pendingRemoval = React.useRef<{
    timer: number;
    claimedIds: string[];
    update: typeof handleUpdateRawTransactions;
  } | null>(null);

  // Run the pending cache removal now instead of on the timer. The animation
  // is presentation; the cache update is the actual consequence of
  // reconciling, so it must survive anything that cuts the animation short.
  // Skipping it would leave claimed rows in a cache-first `reconciled: false`
  // page that nothing is guaranteed to refetch — they would come back looking
  // unreconciled on the next visit to the tab.
  const flushRowRemoval = () => {
    const pending = pendingRemoval.current;
    if (!pending) return;
    window.clearTimeout(pending.timer);
    pendingRemoval.current = null;
    pending.update({toRemove: pending.claimedIds});
  };

  // Unmounting mid-animation (navigating away) is the common way to cut it
  // short. No setLeavingIds here — the component is going away, and the cache
  // is the only thing that outlives it.
  React.useEffect(() => () => flushRowRemoval(), []);

  const scheduleRowRemoval = (claimedIds: string[]) => {
    // A second reconcile landing inside the window is the other way to cut it
    // short: settle the first batch rather than dropping it on the floor.
    flushRowRemoval();
    const update = handleUpdateRawTransactions;
    pendingRemoval.current = {
      claimedIds,
      update,
      timer: window.setTimeout(() => {
        pendingRemoval.current = null;
        update({toRemove: claimedIds});
        setLeavingIds([]);
      }, ROW_EXIT_MS),
    };
  };

  // "Fix up" opens the create-transaction modal prefilled to exactly offset the
  // remaining amount (dated the recorded anchor, with a templated description),
  // then adds the created row to the list and selects it so the session lands
  // balanced and ready to reconcile. Keeping create and reconcile as separate
  // steps (rather than one server call) means a failed create never leaves a
  // stray reconciliation behind — the user just sees the modal error.
  const openFixUpModal = (remainingCents: number) => {
    if (!activeAnchor || accountCurrency == null) return;
    createRawTransactionModal.openModal({
      defaultAccountId: params.id,
      defaultAmount: Money.fromInt(remainingCents, accountCurrency),
      defaultDate: parseDate(activeAnchor.date),
      defaultDescription: fixUpDescription(activeAnchor.date),
      // Stamps the row as an adjustment for this anchor and (with the
      // reconciliationCategory binding set) consumes it on creation — see
      // CreateRawTransactionModal's adjustment mode.
      adjustmentForAnchorId: activeAnchor._id,
      onCreate: (newTransaction) => {
        handleUpdateRawTransactions({toAdd: [newTransaction]});
        setSelected(newTransaction)(true);
      },
    });
  };

  const transactions = data?.allRawTransactions?.items;

  const handleLongPress = React.useCallback(
    (rt: BasicRawTransactionFragment) => {
      navigate(`/raw-transactions/${rt._id}`);
    },
    [navigate],
  );

  // Recording a balance almost always means "as of this row" — reconciling a
  // sparse account is mostly dropping zero anchors at the points where the
  // balance is known. Both entry points (the row hover action and the header
  // button below) prefill the modal's date from a transaction rather than
  // defaulting to today, so the date never has to be typed.
  const openRecordBalance = React.useCallback(
    (date?: string) => {
      recordBalanceAnchorModal.openModal({
        accountId: params.id ?? '',
        currency: account?.currency,
        isCreditCard,
        initialDate: date ? parseDate(date) : undefined,
        onSuccess: () => {
          void refetchAnchors();
          // Recording the newest anchor updates Account.balance server-side.
          void refetchAccount();
        },
      });
    },
    // account?.currency: the account query resolves after first render, and
    // a callback captured with `currency: undefined` would open a foreign
    // wallet's anchor modal in USD units.
    [params.id, isCreditCard, account?.currency],
  );

  // Only prefill dates that can actually take a NEW anchor — the prefill is a
  // shortcut for creating one, and every other case leads somewhere the user
  // didn't ask to go:
  //
  //  - A date that already has an anchor is an edit, not a creation:
  //    recordBalanceAnchor upserts by (account, date), and the modal always
  //    submits source 'manual', so saving would quietly reclassify a statement
  //    anchor and overwrite its balance. That anchor also already renders as
  //    its own row here, so there is nothing to prefill toward.
  //  - A date strictly inside a reconciled span is rejected outright by
  //    assertNotInsideReconciledSpan, so prefilling it only leads to an error
  //    modal. Bounds are exclusive, matching isInsideReconciledSpan — a
  //    boundary date is an existing anchor and caught by the rule above.
  //
  // Gated on anchorsLoaded because "no anchor on this date" is unknowable
  // until the anchors arrive: an empty-because-pending list would call every
  // date free, and a prefilled Save during that window would upsert over a
  // real balance. Same reasoning as the starting-balance affordance above.
  const canRecordBalanceOn = React.useCallback(
    (date: string) => {
      if (!anchorsLoaded) return false;
      if (anchors.some((anchor) => anchor.date === date)) return false;
      return !anchors.some(
        (anchor) =>
          anchor.reconciliation != null &&
          anchor.reconciliation.startDate < date &&
          date < anchor.reconciliation.endDate,
      );
    },
    [anchors, anchorsLoaded],
  );

  // Touch has no hover, so the row action is desktop-only; on mobile the same
  // prefill comes from the selection the user already makes by tapping a row.
  // Exactly one selected row names an unambiguous date — with several selected
  // there is no single answer, so the button falls back to today.
  const selectedDate =
    selected.length === 1 && canRecordBalanceOn(selected[0].date)
      ? selected[0].date
      : undefined;

  // Reconcile-tab actions, kept on the same row as the sync/overflow icons.
  // Record balance seeds an anchor; Add transaction is retained here because
  // reconciling often surfaces a bank row that was never imported, and adding
  // it in place lets the period balance without leaving the flow.
  const reconcileActions =
    params.id && isReconcile ? (
      <>
        <Button
          variant="outline"
          className="h-9 px-3 text-sm"
          title={
            selectedDate
              ? `Record a balance dated ${selectedDate}`
              : 'Record a balance'
          }
          onClick={() => openRecordBalance(selectedDate)}
        >
          Record balance
          {/* The prefilled date rides along so it is visible before the modal
              opens. Dropped below sm: the reconcile header keeps every action
              on one row (stackActionsOnMobile is off), and the date would
              push it into overflow — the modal's own date picker carries it
              there. */}
          {selectedDate && (
            <span className="text-muted-foreground ml-1.5 font-normal tabular-nums max-sm:hidden">
              {selectedDate}
            </span>
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Add transaction"
          onClick={() =>
            createRawTransactionModal.openModal({
              defaultAccountId: params.id,
              onCreate: (newTransaction) =>
                handleUpdateRawTransactions({toAdd: [newTransaction]}),
            })
          }
        >
          <Plus className="size-5" />
        </Button>
      </>
    ) : null;

  let bodyContent: React.ReactNode;
  if (transactions) {
    // Each workflow view surfaces only its own status dimension: the
    // Reconcile view is a timeline of checklist rows and balance anchors,
    // while browse mode shows only allocation exceptions. The timeline
    // renders whenever either side has content — anchors must stay visible
    // (and reconcilable, e.g. a zero-transaction reconcile of an unchanged
    // balance) even when no raw transactions are loaded.
    if (isReconcile && (transactions.length > 0 || anchors.length > 0)) {
      bodyContent = (
        <ReconcileTimeline
          // Expansion state is per-account; navigating to another account
          // starts fresh (session state resets via handleReload).
          key={params.id ?? 'all'}
          isCreditCard={isCreditCard}
          currency={accountCurrency}
          transactions={transactions}
          anchors={anchors}
          hasMoreTransactions={data?.allRawTransactions?.hasMore ?? false}
          selectedIds={selectedIds}
          onClick={toggleRow}
          onLongPress={handleLongPress}
          activeAnchorId={activeAnchorId}
          activeAnchor={activeAnchor}
          activeBaseline={activeBaseline}
          onStartReconcile={setActiveAnchorId}
          completedAnchorIds={completedAnchorIds}
          onCancelReconcile={endSession}
          onFixUp={openFixUpModal}
          anchorsLoaded={anchorsLoaded}
          hasReadingBeforeAnchors={hasReadingBeforeAnchors}
          onAddStartingBalance={(initialDate) =>
            recordBalanceAnchorModal.openModal({
              accountId: params.id ?? '',
              currency: account?.currency,
              isCreditCard,
              initialDate,
              onSuccess: () => {
                void refetchAnchors();
                // Recording the earliest anchor can update Account.balance
                // server-side when it becomes the newest reading.
                void refetchAccount();
              },
            })
          }
          onRecordBalanceAt={openRecordBalance}
          canRecordBalanceOn={canRecordBalanceOn}
          leavingIds={leavingIds}
        />
      );
    } else if (!isReconcile && transactions.length > 0) {
      bodyContent = (
        <RawTransactionList
          transactions={transactions}
          {...gestures.rowProps}
          showProcessedStatus={props.includeProcessed}
        />
      );
    } else {
      bodyContent = (
        <Empty>
          {allocationFilter === 'unallocated'
            ? 'Everything is allocated'
            : 'No transactions'}
        </Empty>
      );
    }
  } else if (error) {
    bodyContent = <div>error</div>;
  } else {
    bodyContent = <Loading size="medium" />;
  }

  // While a session runs the top bar swaps from the account header to the
  // session's own action bar (Fix up / Cancel / Reconcile). Both are built on
  // ActionHeader, so the row keeps the same height — starting a session no
  // longer shifts the list down.
  const header =
    isReconcile && reconcileSessionActive && activeAnchor ? (
      <ReconcileSessionHeader
        anchor={activeAnchor}
        onCancel={endSession}
        onFixUp={openFixUpModal}
      />
    ) : (
      <Header
        selectionState={selectionState}
        actions={selectionActions}
        gestures={isReconcile ? undefined : gestures}
        accountId={params.id}
        extraActions={reconcileActions}
        filterContent={
          <div className="flex flex-wrap items-center gap-2">
            {props.includeProcessed && !isReconcile && (
              <AllocationFilterSelect
                value={allocationFilter}
                onChange={(filter) =>
                  setParams({allocation: filter === 'all' ? null : filter})
                }
              />
            )}
            {!isReconcile && (
              <>
                <DateFilterSelect
                  value={dateFilter}
                  onChange={(filter) =>
                    setParams({
                      startDate: filter.startDate,
                      endDate: filter.endDate,
                    })
                  }
                />
                {/* Bounded width so the row of filters wraps instead of
                    overflowing on narrow screens */}
                <div className="min-w-32 max-w-56 flex-1">
                  <SearchInput
                    // Re-seed the box when the account (and thus URL) changes so
                    // switching accounts clears any typed query.
                    key={params.id ?? 'all'}
                    initialQuery={search}
                    onSearch={(query) => setParams({search: query})}
                    inputProps={{placeholder: 'Search'}}
                  />
                </div>
              </>
            )}
            {activeFilterKeys.length > 0 && (
              <ResetFiltersButton onReset={resetFilters} />
            )}
          </div>
        }
      />
    );

  const scrollTable = (
    <ScrollTable
      className="min-h-0 w-full flex-1"
      scrollStateKey={[
        allocationFilter,
        activeDateFilter.startDate ?? '',
        activeDateFilter.endDate ?? '',
        activeSearch,
      ].join('|')}
      body={
        <>
          {bodyContent}
          {/* Keep the button reachable even when the loaded page has
              emptied (reconciling can claim every loaded row while
              older unreconciled history remains). */}
          {(all.length > 0 || hasMore) && (
            <MoreButton loading={loading} hasMore={hasMore} onMore={handleMore} />
          )}
        </>
      }
      // The running-math summary renders as the list's footer (outside the
      // scroll area) so it holds a fixed spot below the list while a session
      // runs, without ever overlapping the rows the user is matching.
      footer={
        reconcileSessionActive && activeAnchor && activeBaseline ? (
          <ReconcileSessionSummaryFooter
            anchor={activeAnchor}
            baseline={activeBaseline}
            currency={accountCurrency ?? all[0]?.currency ?? 'USD'}
            isCreditCard={isCreditCard}
          />
        ) : undefined
      }
    />
  );

  // Header + list share one column. In reconcile mode the session provider
  // wraps both so its top action bar and summary footer can read the session
  // from context; it stays mounted whether or not a session runs (passing a
  // null anchor while idle) so starting one never remounts the ScrollTable and
  // resets its scroll position. Browse mode never reconciles, so it renders the
  // column directly.
  const inner = (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      {header}
      {scrollTable}
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {isReconcile ? (
        <ReconcileSessionProvider
          accountId={params.id ?? ''}
          anchor={activeAnchor}
          baseline={activeBaseline}
          transactions={transactions ?? []}
          hasMoreTransactions={hasMore}
          loadingMore={loading}
          onLoadMore={handleMore}
          selectedIds={selectedIds}
          onSeedSelection={setSelection}
          onReconciled={(claimedIds) => {
            // The claimed rows leave the unreconciled list, and the anchor
            // gained a reconciliation. Drop the rows from the cached list
            // surgically — reloading would reset to the first page and throw
            // away everything the user paginated in.
            //
            // Mark them leaving first and cut them from the cache only once
            // they've animated out: a whole statement's worth of rows
            // vanishing in one frame reads as a glitch rather than as the
            // result of the reconcile. The anchor refetch runs immediately, so
            // the row turns green while its claimed rows are still collapsing.
            //
            // Between here and the refetch landing, the anchor's own row still
            // reads as unreconciled, so its Reconcile button would come back
            // the moment the session closes — and for the length of the exit
            // the claimed rows are still in the list to be reseeded against it.
            // That is enough to start the same anchor again, rebalance, and
            // submit a second time, which the server rejects outright
            // ("Anchor is already reconciled"). Hold it in a completed state
            // until its refreshed reconciliation arrives.
            const completedId = activeAnchor?._id;
            setActiveAnchorId(null);
            if (completedId) {
              setCompletedAnchorIds((current) =>
                new Set(current).add(completedId),
              );
            }
            clearSelection();
            setLeavingIds(claimedIds);
            void refetchAnchors();
            scheduleRowRemoval(claimedIds);
          }}
        >
          {inner}
        </ReconcileSessionProvider>
      ) : (
        inner
      )}
    </div>
  );
};

export default RawTransactionView;
